import React from 'react'
import { Container, Header, Icon } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import SuperCutoffStep from './SuperCutoffStep'
import { renderStepContent, isStepSelectable } from './SuperCutoffWizard.utils'
import './SuperCutoff.css'

/**
 * Componente presentazionale principale del wizard SuperCutoff.
 * Riceve tutti i dati e le callback via props dal container.
 *
 * Props:
 *   steps           {Array}     Definizione degli step [{ id, title, status }]
 *   activeStepId    {string}    ID dello step attualmente visualizzato
 *   onSelectStep    {Function}  Callback per selezionare uno step dalla sidebar. Firma: fn(stepId)
 *   stepData        {Object}    Dati per ogni step, keyed by stepId
 *   handlers        {Object}    Callback per ogni step, keyed by stepId
 *   loadings        {Object}    Stato loading per ogni operazione, keyed by operationKey
 */
const SuperCutoffWizard = ({
  steps,
  activeStepId,
  onSelectStep,
  stepData,
  handlers,
  loadings,
}) => {
  const activeStep = steps.find(function(s) { return s.id === activeStepId })
  const activeStepIndex = steps.findIndex(function(s) { return s.id === activeStepId })

  if (process.env.NODE_ENV === 'development' && !activeStep) {
    console.warn(
      'SuperCutoffWizard: activeStepId "' + activeStepId + '" non corrisponde a nessuno step nella lista.'
    )
  }

  /**
   * Gestisce la navigazione da tastiera sugli item della sidebar.
   * Attiva la selezione su Enter e Space, coerentemente con il comportamento di role="button".
   *
   * @param {KeyboardEvent} e    - Evento tastiera
   * @param {string}        stepId - ID dello step da selezionare
   */
  const handleStepKeyDown = function(e, stepId) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelectStep(stepId)
    }
  }

  return (
    <Container className="supercutoff-wrapper">
      {/* Header pagina */}
      <div className="supercutoff-header">
        <Header as="h1">
          <Icon name="tasks" aria-hidden="true" />
          <Header.Content>
            Super Cutoff
            <Header.Subheader className="supercutoff-subtitle">
              Guided wizard for the monthly SuperCutoff process — Controlling only
            </Header.Subheader>
          </Header.Content>
        </Header>
      </div>

      <div className="supercutoff-layout">
        {/* Sidebar step list */}
        <nav className="supercutoff-step-list" aria-label="Wizard steps">
          {steps.map(function(step, idx) {
            const isActive = step.id === activeStepId
            const selectable = isStepSelectable(step, activeStepId, steps)
            const lockedLabel = 'Complete the previous step first'

            return (
              <div
                key={step.id}
                role="button"
                tabIndex={selectable ? 0 : -1}
                className={[
                  'supercutoff-step-list-item',
                  isActive ? 'is-active' : '',
                  step.status === 'completed' ? 'is-completed' : '',
                  step.status === 'error' ? 'is-error' : '',
                  !selectable && !isActive ? 'is-locked' : '',
                ].filter(Boolean).join(' ')}
                onClick={selectable ? function() { onSelectStep(step.id) } : undefined}
                onKeyDown={selectable ? function(e) { handleStepKeyDown(e, step.id) } : undefined}
                aria-current={isActive ? 'step' : undefined}
                aria-disabled={(!selectable && !isActive) ? "true" : undefined}
                aria-label={
                  (!selectable && !isActive)
                    ? step.title + ' — ' + lockedLabel
                    : step.title
                }
                style={{ cursor: selectable ? 'pointer' : 'default' }} /* cursor: valore calcolato a runtime */
              >
                <div className="supercutoff-step-number" aria-hidden="true">
                  {step.status === 'completed'
                    ? <Icon name="check" className="supercutoff-sidebar-step-icon" aria-hidden="true" />
                    : step.status === 'error'
                    ? <Icon name="times" className="supercutoff-sidebar-step-icon" aria-hidden="true" />
                    : idx + 1
                  }
                </div>
                <span className="supercutoff-step-list-label">{step.title}</span>
                {!selectable && !isActive && (
                  <Icon name="lock" className="supercutoff-step-lock-icon" aria-hidden="true" />
                )}
              </div>
            )
          })}
        </nav>

        {/* Contenuto step attivo */}
        <div
          className="supercutoff-step-content"
          aria-live="polite"
          aria-atomic="false"
          aria-relevant="additions text"
        >
          {activeStep && (
            <SuperCutoffStep
              stepNumber={activeStepIndex + 1}
              title={activeStep.title}
              status={activeStep.status}
            >
              {renderStepContent(
                activeStep.id,
                stepData[activeStep.id] || {},
                handlers[activeStep.id] || {},
                loadings
              )}
            </SuperCutoffStep>
          )}
        </div>
      </div>
    </Container>
  )
}

SuperCutoffWizard.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['pending', 'in_progress', 'completed', 'error']).isRequired,
  })).isRequired,
  activeStepId: PropTypes.string.isRequired,
  onSelectStep: PropTypes.func.isRequired,
  stepData: PropTypes.object.isRequired,
  handlers: PropTypes.object.isRequired,
  loadings: PropTypes.object.isRequired,
}

export default SuperCutoffWizard
